import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ONBOARDING_CONST, TENANT_CONST } from '@/constant';
import { useTypedSearchParams } from '@/hook';
import { VButton, VChipInput, VFlex, VTextButton, useFlag } from '@/package';
import { useTenantStore } from '@/store';
import type { OnboardingForm, OnboardingSearchParams, Tenant } from '@/type';

const InviteMember = ({ stepEnabled }: { stepEnabled: boolean }) => {
  const { setValue, watch } = useFormContext<OnboardingForm>();
  const { set, flush } = useTypedSearchParams<OnboardingSearchParams>();
  const [userList, setUserList] = useState<string[]>([]);
  const { registerTenant } = useTenantStore();
  const { t } = useTranslation();
  const enterpriseTierStr = t('pricing.enterprise.anguillidae');
  const {
    value: disabledButton,
    setFalse: enableButton,
    setTrue: disableButton,
  } = useFlag(false);

  const registTenant = () => {
    disableButton();

    const email = watch('email');

    const region = watch('region')!;
    const tenantName = watch('tenantName')!;
    const customerAWSAccountId = watch('customerAWSAccountId')!;
    const productCode = watch('productCode')!;
    const inviteList = watch('inviteMembers');

    const companyName = watch('companyName');
    const jobTitle = watch('jobTitle');
    const userName = watch('userName');

    const personalInfoCollection = watch('personalInfoCollection');
    const personalInfoTransfer = watch('personalInfoTransfer');
    const marketingPromotion = watch('marketingPromotion');
    const consentAITraining = watch('consentAITraining');
    const termsOfService = watch('termsOfService');

    const plan = watch('tier.plan');
    const tier: Tenant['tier'] = (() => {
      if (plan === enterpriseTierStr) return TENANT_CONST.TIER.STANDARD;

      return TENANT_CONST.TIER.FREETIER;
    })();

    return registerTenant({
      tenantData: {
        email,
        tenantName,
        tier,
      },
      tenantRegistrationData: {
        registrationStatus: 'In progress',
        customerAWSAccountId,
        productCode,
        inviteList,
        billingUnit: String(
          plan === enterpriseTierStr ? watch('enterpriseBillingUnit') : 1,
        ),
        companyName,
        region,
        jobTitle,
        userName,
        personalInfoCollection: `${personalInfoCollection ?? false}`,
        personalInfoTransfer: `${personalInfoTransfer ?? false}`,
        marketingPromotion: `${marketingPromotion ?? false}`,
        consentAITraining: `${consentAITraining ?? false}`,
        termsOfService: `${termsOfService ?? false}`,
      },
    })
      .then(() => {
        set('step', ONBOARDING_CONST.ONBOARDING_STEP_QUERY_PARAM.COMPLETE);
        flush();
      })
      .finally(enableButton);
  };

  const onInviteButtonClick = () => {
    return registTenant();
  };

  const onSkipButtonClick = () => {
    return registTenant();
  };

  useEffect(() => {
    setValue('inviteMembers', userList.join(','));
  }, [userList]);

  useEffect(() => {
    if (!stepEnabled) {
      set('step', ONBOARDING_CONST.ONBOARDING_STEP_QUERY_PARAM.CHOOSE_PLAN);
      flush();
    }
  }, [watch('tier'), watch('tenantName')]);

  return (
    <VFlex direction="col" className="justify-center gap-[30px] pt-6">
      <VFlex direction="col" className="w-[296px] items-center gap-4">
        <VChipInput
          label="email"
          fluid
          value={userList}
          onChange={userEmailList => setUserList(userEmailList)}
          valueValidator={/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/}
          autoFocus
        />

        <VButton
          className="capitalize"
          disabled={disabledButton}
          fluid
          type="button"
          onClick={onInviteButtonClick}
        >
          invite
        </VButton>
      </VFlex>

      <div className="inline-flex cursor-pointer items-center justify-center px-1 py-0.5">
        <VTextButton
          fluid
          disabled={disabledButton}
          variant="primary"
          onClick={onSkipButtonClick}
        >
          Skip
        </VTextButton>
      </div>
    </VFlex>
  );
};

export default InviteMember;
